/**
 * React Query hook + helpers for Reports > Item File.
 *
 *   useItemFile(filters, sort, enabled)   GET /v1/retail/item-file
 *   downloadItemFileXlsx(filters, sort)   GET /v1/retail/item-file/export
 *
 * The item file Tim feeds to a downstream system, one row per item. The api filters and
 * sorts, and cuts a very long list to its first 5000 rows (`total_rows` always covers every
 * match, and the Excel export carries every row), so the page renders the rows as given and
 * sends the sort to the api. (Store stock moved to Commerce > Inventory.)
 *
 * Contract:
 *
 *   GET /v1/retail/item-file
 *     ?status=...         optional, repeatable item status code (A, D, V)
 *     &department=...     optional, repeatable
 *     &vendor=...         optional, repeatable vendor number
 *     &q=...              optional text for the item, description, UPC or vendor
 *     &sort=...&dir=...   optional column and direction; default is the item number
 *   -> { as_of, rows, total_rows, truncated, statuses, departments, vendors }
 *
 * The row types come from `@ampersandbrands/ui`, because the shared screen owns the wire
 * shape. Same 404/503 "not deployed yet" convention as the other report hooks.
 */

import { useQuery } from '@tanstack/react-query';
import type {
  ItemFileFilters,
  ItemFileOption,
  ItemFileRow,
  ItemFileSort,
  ItemFileSortColumn,
} from '@ampersandbrands/ui';
import { apiGet } from './api';
import { RETAIL_API_BASE } from './useRetailStores';

export const ITEM_FILE_API_BASE = `${RETAIL_API_BASE}/item-file`;

export const EMPTY_ITEM_FILTERS: ItemFileFilters = {
  status: [],
  department: [],
  vendor: [],
  q: '',
};

/** The default order: item number (the api's own default). */
export const DEFAULT_ITEM_SORT: ItemFileSort = { col: null, dir: 'asc' };

const ITEM_SORT_COLUMNS: readonly ItemFileSortColumn[] = [
  'item_no',
  'descr',
  'status',
  'department',
  'vendor_name',
  'prc_1',
  'lst_cost',
  'upc',
];

export type ItemFileResponse = {
  as_of: string | null;
  rows: ItemFileRow[];
  total_rows: number;
  truncated: boolean;
  statuses: ItemFileOption[];
  departments: ItemFileOption[];
  vendors: ItemFileOption[];
};

// ---------------------------------------------------------------------------
// Query string (what the api gets)
// ---------------------------------------------------------------------------

/** Query string for the item report and its export. Lists repeat their key. */
export function buildItemsQs(filters: ItemFileFilters, sort: ItemFileSort): string {
  const sp = new URLSearchParams();
  for (const s of filters.status) sp.append('status', s);
  for (const d of filters.department) sp.append('department', d);
  for (const v of filters.vendor) sp.append('vendor', v);
  if (filters.q) sp.set('q', filters.q);
  if (sort.col) {
    sp.set('sort', sort.col);
    sp.set('dir', sort.dir);
  }
  return sp.toString();
}

// ---------------------------------------------------------------------------
// The page's URL (what a shared link carries)
// ---------------------------------------------------------------------------

/** Read the filters back out of the page's URL. */
export function parseItemFilters(sp: URLSearchParams): ItemFileFilters {
  return {
    status: sp.getAll('status').filter(Boolean),
    department: sp.getAll('department').filter(Boolean),
    vendor: sp.getAll('vendor').filter(Boolean),
    q: (sp.get('q') ?? '').trim(),
  };
}

/** Read the sort back out of the page's URL; an unknown column means the default order. */
export function parseItemSort(sp: URLSearchParams): ItemFileSort {
  const col = sp.get('sort');
  if (!col || !(ITEM_SORT_COLUMNS as readonly string[]).includes(col)) return DEFAULT_ITEM_SORT;
  return { col: col as ItemFileSortColumn, dir: sp.get('dir') === 'desc' ? 'desc' : 'asc' };
}

/** Write the filters into the page's URL, leaving defaults out. */
export function writeItemFilters(sp: URLSearchParams, filters: ItemFileFilters): void {
  for (const k of ['status', 'department', 'vendor', 'q']) sp.delete(k);
  for (const s of filters.status) sp.append('status', s);
  for (const d of filters.department) sp.append('department', d);
  for (const v of filters.vendor) sp.append('vendor', v);
  if (filters.q) sp.set('q', filters.q);
}

/** Write the sort into the page's URL, leaving the default order out. */
export function writeItemSort(sp: URLSearchParams, sort: ItemFileSort): void {
  sp.delete('sort');
  sp.delete('dir');
  if (sort.col) {
    sp.set('sort', sort.col);
    sp.set('dir', sort.dir);
  }
}

// ---------------------------------------------------------------------------
// Reading the response
// ---------------------------------------------------------------------------

function arr<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

/** Tolerant read, so a partial or absent payload degrades to an empty report rather than a crash. */
export function normalizeItemFile(raw: unknown): ItemFileResponse {
  const r = (raw ?? {}) as Record<string, unknown>;
  const rows = arr<ItemFileRow>(r.rows);
  return {
    as_of: typeof r.as_of === 'string' ? r.as_of : null,
    rows,
    total_rows: typeof r.total_rows === 'number' ? r.total_rows : rows.length,
    truncated: r.truncated === true,
    statuses: arr<ItemFileOption>(r.statuses),
    departments: arr<ItemFileOption>(r.departments),
    vendors: arr<ItemFileOption>(r.vendors),
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useItemFile(filters: ItemFileFilters, sort: ItemFileSort, enabled: boolean) {
  const query = useQuery<ItemFileResponse, Error>({
    queryKey: ['retail', 'item-file', filters, sort],
    queryFn: async () => {
      const qs = buildItemsQs(filters, sort);
      return normalizeItemFile(await apiGet<unknown>(qs ? `${ITEM_FILE_API_BASE}?${qs}` : ITEM_FILE_API_BASE));
    },
    staleTime: 60_000,
    placeholderData: (prev) => prev,
    retry: false,
    enabled,
  });
  const data = query.data;
  return {
    asOf: data?.as_of ?? null,
    rows: data?.rows ?? [],
    totalRows: data?.total_rows ?? 0,
    statuses: data?.statuses ?? [],
    departments: data?.departments ?? [],
    vendors: data?.vendors ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

// ---------------------------------------------------------------------------
// Download
// ---------------------------------------------------------------------------

/** Browser download of the Excel export, following the filters and sort on screen. */
export async function downloadItemFileXlsx(filters: ItemFileFilters, sort: ItemFileSort): Promise<void> {
  const qs = buildItemsQs(filters, sort);
  const res = await fetch(`${ITEM_FILE_API_BASE}/export${qs ? `?${qs}` : ''}`, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    // The api answers a file that is too big for one response with a plain sentence; show that, not the status line.
    let message = '';
    try {
      message = (JSON.parse(text) as { error?: { message?: string } }).error?.message ?? '';
    } catch {
      message = '';
    }
    throw new Error(message || `Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const m = (res.headers.get('content-disposition') ?? '').match(/filename="([^"]+)"/);
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = m?.[1] ?? 'item-file.xlsx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}
